import { getAuthUrl } from 'src/contstant/apiEndpoints';

/**
 * Anchor service for the auth recovery fallback.
 *
 * Matches v1's `AuthHandler` catch branch: `window.location.href = getAuthUrl('orbit')`.
 *
 * NOTE: ALB session cookies are per-service (`AWSELBAuthSessionCookie-<Svc>`),
 * so a top-level hop to Orbit's `/ping` establishes ONLY Orbit's session.
 * v1 uses this as a last resort when iframe warm-up fails entirely.
 */
const AUTH_RECOVERY_ANCHOR = 'orbit' as const;

/**
 * Session-storage key that tracks whether we've already burned the
 * one-time recovery flow this session.
 */
const RECOVERY_GUARD_KEY = 'v2.mobile.auth.recoveryAttempted';

/**
 * Returns whether the recovery flow has already been attempted this session.
 */
export function hasAttemptedAuthRecovery(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(RECOVERY_GUARD_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Clear the recovery guard after auth successfully completes.
 */
export function clearAuthRecoveryGuard(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RECOVERY_GUARD_KEY);
  } catch {
    // Best-effort.
  }
}

/**
 * Recover the auth chain by navigating THIS tab to the anchor service's
 * `/ping` URL — identical to v1's `AuthHandler` catch branch:
 *
 *   `window.location.href = getAuthUrl('orbit')`
 *
 * This is a full top-level navigation to a cross-origin service endpoint.
 * The browser follows the Midway OIDC redirect chain at the top level,
 * which establishes the ALB session cookie for that service.
 *
 * The `sessionStorage` guard is marked BEFORE navigating so a
 * genuinely-broken auth environment can't loop the user.
 */
export function startAuthRecovery(): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(RECOVERY_GUARD_KEY, '1');
  } catch {
    // One extra attempt beats locking the user out of recovery entirely.
  }

  // Match v1's AuthHandler catch: top-level navigation to orbit /ping.
  window.location.assign(getAuthUrl(AUTH_RECOVERY_ANCHOR));
}
